import { useEffect, useState, useMemo } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion, AnimatePresence } from "motion/react";

// Animation Studio capability images
import imgAnamorphic from "../../assets/AnimationStudioImages/3d_anamorphic.png";
import imgAnimation from "../../assets/AnimationStudioImages/3d_animation.png";
import imgMapping from "../../assets/AnimationStudioImages/3d_mapping.png";
import imgMotionGraphics from "../../assets/AnimationStudioImages/3d_motion_graphics.png";
import AiPoweredFilms from "../../assets/AnimationStudioImages/AI_Powered_Films.png";

// Technology capability images
import imgImmersiveTech from "../../assets/TechnoloyImages/immersive_tech.png";
import imgAiSystems from "../../assets/TechnoloyImages/ai_powered_systems.png";
import imgSoftwareSolutions from "../../assets/TechnoloyImages/software_solutions.png";

// Hardware Solutions capability images
import imgLedScreen from "../../assets/HardwareSolution/led_screen.jpg";
import imgHolographicTubes from "../../assets/HardwareSolution/holographic_tubes.png";
import imgTouchStandees from "../../assets/HardwareSolution/touch_screen_standees.png";
import imgTouchKiosks from "../../assets/HardwareSolution/touch_screen_kiosks.png";
import imgCameras from "../../assets/HardwareSolution/cameras.png";

const serviceData: Record<string, any> = {
  "3d-animation": {
    id: "3d-animation",
    title: "Animation Studio",
    accent: "text-teal-600 dark:text-teal-400",
    bgAccent: "bg-teal-500/10",
    hero: "Cinematic 3D motion, mapping, and illusions.",
    description:
      "KIVAS transforms ideas into stunning visual experiences producing high-impact 3D content for screens of every scale, from social feeds to stadium facades. Whether it's an optical illusion on a city billboard, motion graphics for a product launch, or synchronized visuals for a live concert every piece is crafted to command attention, spark conversation, and leave a lasting impression.",
    capabilitiesTitle: "Capabilities",
    capabilities: [
      {
        name: "3D Anamorphic",
        desc: "Captivating 3D anamorphic visuals designed for large-format screens and public displays. We create illusions that stop people mid stride and make your brand impossible to ignore.",
        img: imgAnamorphic,
      },
      {
        name: "3D Animation",
        desc: "From cinematic films to high-energy stage visuals for brands, corporate events, concerts and live activations, we produce 3D animation content that engages audiences at any scale.",
        img: imgAnimation,
      },
      {
        name: "3D mapping",
        desc: "We turn buildings, stages, and surfaces into living canvases. Using precision projection, we map dynamic visuals onto any structure — for product launches, brand activations, public art installations, and large-scale events.",
        img: imgMapping,
      },
      {
        name: "3D Motion Graphics",
        desc: "From event openers and brand identity films to product explainers and Digital signage — we craft motion graphics that transform every message into a visual story, moving audiences across every screen, stage, and space.",
        img: imgMotionGraphics,
      },
      {
        name: "AI-Powered Films",
        desc: "We create AI-generated films for brand campaigns, product launches, advertisements, corporate storytelling, and social media. From concept and scriptwriting to AI visuals, realistic voiceovers, lip-sync, motion graphics, VFX, editing, and final delivery, we combine creative storytelling with advanced AI filmmaking to deliver cinematic, high-impact experiences across digital platforms, events, and exhibitions.",
        img: AiPoweredFilms,
      },
    ],
  },
  "ar-vr": {
    id: "ar-vr",
    title: "Technology",
    accent: "text-blue-600 dark:text-blue-400",
    bgAccent: "bg-blue-500/10",
    hero: "Immersive XR, AI systems, and custom software.",
    description:
      "KIVAS excels in engineering intelligent digital solutions tailored to the unique demands of every industry — building immersive digital experiences, powerful software, seamless applications and tools that simplify operations. From custom enterprise platforms to AI models that process live data as it happens, every system KIVAS builds is designed to perform in real time, in real space, in front of real audiences — across web, mobile, and immersive environments.",
    capabilities: [
      {
        name: "Immersive Tech",
        intro:
          "KIVAS designs and develops Augmented Reality, Virtual Reality, and Extended Reality experiences that blur the line between the physical and digital world.",
        points: [
          {
            tag: "AR",
            title: "Augmented Reality",
            desc: "Layering digital intelligence over the real world — for product visualisation, brand activations, training simulations, and interactive marketing experiences across mobile and spatial environments.",
          },
          {
            tag: "VR",
            title: "Virtual Reality",
            desc: "Fully immersive virtual environments built for training, product demonstrations, experiential marketing, and spatial storytelling — designed for headsets and large-scale installations alike.",
          },
          {
            tag: "XR",
            title: "Extended Reality",
            desc: "Beyond augmented and virtual — XR is where physical and digital worlds merge entirely. We craft extended reality experiences that redefine how brands, industries, and audiences engage with the world around them.",
          },
        ],
        img: imgImmersiveTech,
      },
      {
        name: "AI Powered Systems",
        intro:
          "The most powerful technology is the kind that thinks for itself. We develop AI-powered systems designed to automate, analyse, and elevate operations at every level. We bring AI off the whiteboard and into your workflow.",
        points: [
          {
            tag: "Vision",
            title: "Vision & Recognition",
            desc: "Facial recognition, image recognition, object detection, emotion detection, crowd analytics, and gesture recognition.",
          },
          {
            tag: "Voice",
            title: "Speech & Language",
            desc: "Speech to text, voice recognition, AI chatbots and virtual assistants, sentiment analysis, and multilingual processing.",
          },
          {
            tag: "Ops",
            title: "Automation & Intelligence",
            desc: "Workflow automation, predictive analytics, data management, recommendation engines, and operational automation.",
          },
          {
            tag: "GenAI",
            title: "Generative AI",
            desc: "AI content generation, AI image and video generation, AI avatars and virtual hosts, and personalisation engines.",
          },
        ],
        img: imgAiSystems,
      },
      {
        name: "Software Solutions",
        intro:
          "Every business operates differently that's why we engineer software solutions tailored to fit your exact workflow, industry, and ambition.",
        points: [
          {
            tag: "Web",
            title: "Web-Based Applications",
            desc: "From Business Management Systems and event management platforms to inventory management tools, CRM solutions, data dashboards, and workflow automation software — engineered to handle the complexity of real business operations.",
          },
          {
            tag: "Mobile",
            title: "Mobile-Based Applications",
            desc: "From customer-facing iOS and Android applications to B2B platforms, B2C apps, internal workforce management tools, and hybrid mobile solutions — intuitive, high-performing, and built around how your users actually think and work.",
          },
        ],
        img: imgSoftwareSolutions,
      },
    ],
  },
  hardware: {
    id: "hardware",
    title: "Hardware Solutions",
    capabilitiesTitle: "Our Products",
    accent: "text-orange-600 dark:text-orange-400",
    bgAccent: "bg-orange-500/10",
    hero: "Engineering the physical interface.",
    description:
      "Every extraordinary experience demands an extraordinary foundation. KIVAS deploys and integrates premium display and interactive hardware the physical infrastructure that brings every visual, digital, and immersive experience to life. *Available for both rental and purchase.",
    capabilities: [
      {
        name: "LED Screens",
        desc: "High-resolution, custom LED display panels engineered for vibrant indoor and outdoor environments.",
        img: imgLedScreen,
      },
      {
        name: "Holographic Tubes",
        desc: "Cutting-edge 3D holographic display tubes designed to create mesmerizing product showcases and visual illusions.",
        img: imgHolographicTubes,
      },
      {
        name: "Touch Screen Standees",
        desc: "Interactive digital signage and wayfinding standees with responsive multi-touch capabilities for public spaces.",
        img: imgTouchStandees,
      },
      {
        name: "Touch Screen Kiosks",
        desc: "Sleek, self-service interactive kiosks built for seamless user engagement, registration, and information delivery.",
        img: imgTouchKiosks,
      },
      {
        name: "Cameras",
        desc: "Advanced tracking, sensing, and cinematic camera systems integrated into interactive and immersive setups.",
        img: imgCameras,
      },
    ],
  },
};

export default function ServicePage() {
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState(id || "3d-animation");
  const [isNavVisible, setIsNavVisible] = useState(true);

  const orderedServices = [
    serviceData["3d-animation"],
    serviceData["ar-vr"],
    serviceData["hardware"],
  ];

  // Synchronized scroll direction listener to coordinate with Navbar
  useEffect(() => {
    let prevY = window.scrollY;

    const handleScroll = () => {
      const currentY = window.scrollY;
      if (currentY < 60) {
        setIsNavVisible(true);
      } else if (currentY > prevY && currentY - prevY > 6) {
        setIsNavVisible(false);
      } else if (currentY < prevY && prevY - currentY > 6) {
        setIsNavVisible(true);
      }
      prevY = currentY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Initial scroll on mount or URL param change
  useEffect(() => {
    if (id && serviceData[id]) {
      setActiveTab(id);
      const timer = setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          const y = el.getBoundingClientRect().top + window.scrollY - 180;
          window.scrollTo({ top: y, behavior: "smooth" });
        }
      }, 100);
      return () => clearTimeout(timer);
    } else if (!id) {
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [id]);

  // Scroll Spy to highlight the tab corresponding to the section in view
  useEffect(() => {
    const sections = ["3d-animation", "ar-vr", "hardware"];

    const handleScroll = () => {
      const scrollPos = window.scrollY + 240;

      for (let i = sections.length - 1; i >= 0; i--) {
        const sectionId = sections[i];
        const el = document.getElementById(sectionId);
        if (el && el.offsetTop <= scrollPos) {
          setActiveTab(sectionId);
          if (window.location.pathname !== `/services/${sectionId}`) {
            window.history.replaceState(null, "", `/services/${sectionId}`);
          }
          break;
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    setActiveTab(sectionId);
    if (window.location.pathname !== `/services/${sectionId}`) {
      window.history.replaceState(null, "", `/services/${sectionId}`);
    }
    const el = document.getElementById(sectionId);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 180;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  if (!id || !serviceData[id]) {
    return <Navigate to="/technology" replace />;
  }

  const tabs = [
    { id: "3d-animation", label: "3D Animation" },
    { id: "ar-vr", label: "Technology" },
    { id: "hardware", label: "Hardware Solutions" },
  ];

  return (
    <main className="kivas-service-page relative pt-32 pb-40 min-h-screen bg-[#fcfcfc] dark:bg-[#050505]">
      {/* Global Noise & Texture */}
      <div className="pointer-events-none fixed inset-0 z-50 h-full w-full opacity-[0.025] mix-blend-color-burn dark:opacity-[0.05] dark:mix-blend-color-dodge">
        <svg className="absolute inset-0 h-full w-full">
          <filter id="noiseFilter">
            <feTurbulence
              type="fractalNoise"
              baseFrequency="0.75"
              numOctaves="3"
            />
          </filter>
          <rect width="100%" height="100%" filter="url(#noiseFilter)" />
        </svg>
      </div>
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.03)_1px,transparent_1px)] dark:bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:100px_100px] [mask-image:radial-gradient(ellipse_100%_100%_at_50%_0%,#000_30%,transparent_100%)]" />
      </div>

      <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 relative z-10">
        {/* Prominent Page Filters */}
        <div
          className={`mb-24 flex flex-col items-center sticky z-50 pt-2 transition-[top] duration-300 ease-out ${
            isNavVisible ? "top-24" : "top-6"
          }`}
        >
          <div className="absolute inset-0 -top-24 h-48 bg-gradient-to-b from-[#fcfcfc] via-[#fcfcfc] to-transparent dark:from-[#050505] dark:via-[#050505] pointer-events-none -z-10" />
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-col md:flex-row justify-center p-1.5 rounded-3xl md:rounded-full bg-white/60 dark:bg-black/60 border border-black/10 dark:border-white/10 backdrop-blur-3xl w-full md:w-auto shadow-[0_8px_32px_rgba(0,0,0,0.06)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.4)]"
          >
            {tabs.map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => scrollToSection(tab.id)}
                  className={`relative px-6 py-3 md:px-8 md:py-3 rounded-2xl md:rounded-full font-sans text-sm md:text-[15px] font-normal tracking-wide transition-all duration-500 interactive w-full md:w-auto text-center cursor-pointer ${
                    isActive
                      ? "bg-[#fcfcfc] dark:bg-[#111] text-black dark:text-white shadow-sm ring-1 ring-black/5 dark:ring-white/10 font-semibold"
                      : "text-black/60 dark:text-white/50 hover:text-black dark:hover:text-white hover:bg-black/5 dark:hover:bg-white/5 ring-1 ring-transparent"
                  }`}
                >
                  {tab.label}
                </button>
              );
            })}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-col gap-32"
        >
          {orderedServices.map((data, serviceIndex) => (
            <div
              id={data.id}
              key={data.id}
              className={`relative ${serviceIndex > 0 ? "pt-24 mt-8 border-t border-black/10 dark:border-white/10" : "pt-8"}`}
            >
              {/* Ambient Glow */}
              <div className="absolute top-0 right-0 w-full h-full max-w-[800px] max-h-[800px] pointer-events-none overflow-hidden z-[-1]">
                <div
                  className="absolute top-1/4 -right-1/4 w-[600px] h-[600px] rounded-full blur-[100px] opacity-40 dark:opacity-30 mix-blend-screen transition-all duration-1000"
                  style={{
                    background:
                      data.id === "3d-animation"
                        ? "radial-gradient(circle, rgba(20, 184, 166, 0.45) 0%, transparent 70%)"
                        : data.id === "hardware"
                          ? "radial-gradient(circle, rgba(249, 115, 22, 0.45) 0%, transparent 70%)"
                          : "radial-gradient(circle, rgba(59, 130, 246, 0.45) 0%, transparent 70%)",
                  }}
                />
              </div>

              {/* Hero Section */}
              <div className="mb-24 max-w-5xl relative z-10">
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  className={`inline-flex font-Montserrat text-xs tracking-widest uppercase ${data.accent} mb-8 items-center gap-4 px-4 py-2 rounded-full border border-current shadow-sm bg-white/50 dark:bg-black/50 backdrop-blur-md`}
                >
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 bg-current"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-current"></span>
                  </span>
                  {data.title}
                </motion.div>

                <h1 className="font-Montserrat text-5xl md:text-7xl lg:text-[6rem] font-bold tracking-tighter leading-tight mb-16 text-transparent bg-clip-text bg-gradient-to-br from-black via-black/90 to-black/50 dark:from-white dark:via-white/90 dark:to-white/50 drop-shadow-sm">
                  {data.hero}
                </h1>

                <p className="font-Montserrat text-base md:text-lg text-black/70 dark:text-white/70 leading-relaxed text-justify max-w-4xl">
                  {data.description}
                </p>
              </div>

              {/* Capabilities Grid */}
              <div>
                <h3 className="font-Montserrat text-xs uppercase tracking-widest text-black/60 dark:text-white/60 mb-12">
                  {data.capabilitiesTitle || "Capabilities"}
                </h3>
                {data.id === "ar-vr" ? (
                  <div className="relative flex flex-col gap-16 md:gap-32 pt-8 pb-16">
                    {data.capabilities.map((cap: any, i: number) => (
                      <div
                        key={`${data.id}-${cap.name}-wrapper`}
                        className="kivas-service-sticky-card sticky w-full transition-[top] duration-300 ease-out"
                        style={{
                          top: `calc(${isNavVisible ? "160px" : "90px"} + ${i * 15}px)`,
                          zIndex: i + 10,
                        }}
                      >
                        <motion.div
                          initial={{ y: 80, opacity: 0 }}
                          whileInView={{ y: 0, opacity: 1 }}
                          viewport={{ once: true, margin: "-50px" }}
                          transition={{
                            duration: 0.8,
                            ease: [0.16, 1, 0.3, 1],
                          }}
                          className="kivas-service-capability-card group w-full border border-black/10 dark:border-white/10 rounded-[2.5rem] bg-white/70 dark:bg-[#0a0a0a]/70 backdrop-blur-2xl overflow-hidden flex flex-col xl:flex-row shadow-[0_10px_40px_rgba(0,0,0,0.05)] dark:shadow-[0_10px_40px_rgba(0,0,0,0.5)] xl:h-[570px]"
                        >
                          <div className="absolute inset-0 bg-gradient-to-br from-white/40 to-white/0 dark:from-white/5 dark:to-white/0 pointer-events-none z-20" />
                          <div className="kivas-service-media relative w-full xl:w-5/12 h-64 md:h-80 xl:h-full overflow-hidden shrink-0">
                            <motion.img
                              src={cap.img}
                              alt={cap.name}
                              className="absolute inset-0 w-full h-full object-cover transition-transform duration-[2s] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-105"
                              referrerPolicy="no-referrer"
                            />
                            <div className="absolute inset-0 bg-gradient-to-tr from-black/40 via-transparent to-transparent mix-blend-overlay" />
                          </div>
                          <div className="w-full xl:w-7/12 h-full p-6 sm:p-7 md:p-8 xl:py-7 xl:px-10 flex flex-col justify-center relative z-10 border-t xl:border-t-0 xl:border-l border-black/5 dark:border-white/5">
                            <h3 className="font-Montserrat text-2xl md:text-3xl xl:text-4xl font-bold text-black dark:text-white mb-3 tracking-tight">
                              {cap.name}
                            </h3>

                            {/* Intro paragraph with stylish accent left-border */}
                            {cap.intro && (
                              <p className="font-Montserrat text-xs md:text-sm text-black/80 dark:text-white/80 leading-relaxed pl-3.5 border-l-2 border-blue-500/60 mb-4 text-justify">
                                {cap.intro}
                              </p>
                            )}

                            {/* Impressive sub-feature micro-cards in a vertical list */}
                            {cap.points && (
                              <div className="flex flex-col gap-2.5">
                                {cap.points.map((pt: any, ptIdx: number) => (
                                  <div
                                    key={ptIdx}
                                    className="group/item relative py-2.5 px-3.5 md:py-2.5 md:px-4 rounded-xl md:rounded-2xl bg-black/[0.02] dark:bg-white/[0.02] border border-black/5 dark:border-white/[0.08] hover:border-blue-500/40 hover:bg-black/[0.04] dark:hover:bg-white/[0.05] transition-all duration-300 shadow-xs"
                                  >
                                    <div className="flex items-center justify-between gap-3 mb-1">
                                      <div className="flex items-center gap-2.5">
                                        <span className="flex items-center justify-center w-5.5 h-5.5 rounded-full bg-blue-500/10 text-blue-600 dark:text-blue-400 text-[11px] font-mono font-bold shrink-0">
                                          0{ptIdx + 1}
                                        </span>
                                        <h4 className="font-Montserrat text-xs md:text-[14px] font-bold text-black dark:text-white group-hover/item:text-blue-600 dark:group-hover/item:text-blue-400 transition-colors">
                                          {pt.title}
                                        </h4>
                                      </div>
                                      {pt.tag && (
                                        <span className="px-2 py-0.5 rounded-full text-[9px] font-mono font-semibold uppercase tracking-widest bg-black/5 dark:bg-white/10 text-black/60 dark:text-white/60 shrink-0">
                                          {pt.tag}
                                        </span>
                                      )}
                                    </div>
                                    <p className="font-Montserrat text-xs text-black/60 dark:text-white/60 leading-relaxed pl-8 text-justify">
                                      {pt.desc}
                                    </p>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                          <div
                            className={`absolute inset-0 border-2 rounded-[2.5rem] opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-700 ${data.accent.replace("text-", "border-").split(" ")[0]} ${data.accent.replace("dark:text-", "dark:border-").split(" ")[1] || ""} mix-blend-overlay z-20`}
                          />
                        </motion.div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {data.capabilities.map((cap: any, i: number) => (
                      <motion.div
                        key={`${data.id}-${cap.name}`}
                        initial={{ y: 80, opacity: 0, scale: 0.95 }}
                        whileInView={{ y: 0, opacity: 1, scale: 1 }}
                        viewport={{ once: true, margin: "-50px" }}
                        transition={{
                          duration: 1.0,
                          ease: [0.16, 1, 0.3, 1],
                          delay: i * 0.15,
                        }}
                        whileHover={{
                          y: -10,
                          transition: { duration: 0.4, ease: "easeOut" },
                        }}
                        className="kivas-service-capability-card group relative border border-black/10 dark:border-white/10 rounded-3xl bg-white/40 dark:bg-black/40 backdrop-blur-xl hover:bg-white/60 dark:hover:bg-white/5 transition-all duration-500 overflow-hidden flex flex-col shadow-[0_8px_30px_rgba(0,0,0,0.04)] dark:shadow-[0_8px_30px_rgba(0,0,0,0.4)] hover:shadow-[0_20px_40px_rgba(0,0,0,0.08)] dark:hover:shadow-[0_20px_40px_rgba(0,0,0,0.6)]"
                      >
                        <div className="absolute inset-0 bg-gradient-to-br from-white/40 to-white/0 dark:from-white/5 dark:to-white/0 pointer-events-none z-20" />
                        <div className="kivas-service-media relative w-full h-56 md:h-72 overflow-hidden bg-black/5 dark:bg-white/5">
                          <motion.img
                            src={cap.img}
                            alt={cap.name}
                            initial={{ scale: 1.2 }}
                            whileInView={{ scale: 1 }}
                            transition={{
                              duration: 1.5,
                              ease: [0.16, 1, 0.3, 1],
                            }}
                            className="absolute inset-0 w-full h-full object-cover transition-transform duration-[2s] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-110"
                            referrerPolicy="no-referrer"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-700" />
                        </div>
                        <div className="p-8 md:p-10 flex flex-col flex-grow relative z-10">
                          <h3 className="font-Montserrat text-3xl font-bold text-black dark:text-white mb-4 transform translation-y-0 group-hover:-translate-y-1 transition-transform duration-500">
                            {cap.name}
                          </h3>
                          <p className="font-Montserrat text-black/60 dark:text-white/60 leading-relaxed text-xs md:text-sm whitespace-pre-line text-justify">
                            {cap.desc}
                          </p>
                        </div>
                        <div
                          className={`absolute inset-0 border-2 rounded-3xl opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-700 ${data.accent.replace("text-", "border-").split(" ")[0]} ${data.accent.replace("dark:text-", "dark:border-").split(" ")[1] || ""} mix-blend-overlay`}
                        />
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </main>
  );
}
